import React, { useMemo } from 'react';
import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';

import {
  Core_Ad_Banner_WrapperQuery,
  Core_Ad_Banner_WrapperQueryVariables,
  Core_Marketplace_Remixer_SearchQuery,
  Core_Marketplace_Remixer_SearchQueryVariables,
  MarketplaceQuerySuggestionsQuery,
  MarketplaceQuerySuggestionsQueryVariables,
} from '@reverbdotcom/commons/src/gql/graphql';

import ListingGrid from '../../listing_grid';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import RQLPagination from '@reverbdotcom/commons/src/components/rql_pagination';
import { getOffsetFromPage } from '../../../pagination_helpers';
import { QUERY_SUGGESTIONS_QUERY } from '../../with_query_suggestions';
import MarketplaceRelatedSearches from '../../discovery/related_searches';
import HeaderWithMetadata from '../header_with_metadata';
import { BumpDSACompliancePopover } from '@reverbdotcom/commons/src/components/bump_dsa_compliance_popover';
import { adBannerQuery } from '@reverbdotcom/commons/src/components/ads/ad_fragments';
import { parseKeywords } from '@reverbdotcom/commons/src/components/ads/ad_banner_helpers';
import { AdBanner } from '@reverbdotcom/commons/src/components/ads/ad_banner';
import SearchOverview from './search_overview';
import { getItem, setItem } from '@reverbdotcom/commons/src/local_storage_utils';
import { LIST_VIEW_STATE } from '@reverbdotcom/commons/src/components/list_view_toggle';

function v0Input(url) {
  const urlSearchParams = new URLSearchParams(url.search);
  return {
    query: url.query?.query || '',
    sort: url.query?.sort,
    page: parseInt(url.query?.page, 10) || 1,
    pageSize: 45,
    currentSearchParams: urlSearchParams.toString(),
    filters: [],
  };
}

function MarketplacePageV2() {
  const url = useURL();
  const mappedInput = v0Input(url);
  const [listViewEnabled, setListViewEnabled] = React.useState(getItem(LIST_VIEW_STATE));

  const remixerSearchResults = useQuery<
    Core_Marketplace_Remixer_SearchQuery,
    Core_Marketplace_Remixer_SearchQueryVariables
  >(
    MARKETPLACE_REMIXER_SEARCH,
    {
      ssr: false,
      variables: {
        input: {
          searchRemixerV0Input: mappedInput,
        },
      },
    },
  );

  const marketplaceListingsSearchData = useMemo(() => {
    return remixerSearchResults.data ?? remixerSearchResults.previousData;
  }, [remixerSearchResults]);

  const { marketplaceListingsSearch } = marketplaceListingsSearchData ?? {};

  const {
    data: querySuggestionsResponse, loading: querySuggestionsLoading,
  } = useQuery<MarketplaceQuerySuggestionsQuery, MarketplaceQuerySuggestionsQueryVariables>(
    QUERY_SUGGESTIONS_QUERY,
    {
      ssr: false,
      variables: {
        searchQueries: url.query?.query ? [
          {
            query: url.query?.query || '',
          },
        ] : [],
      },
    },
  );

  const {
    data: adResponse, loading: adLoading,
  } = useQuery<Core_Ad_Banner_WrapperQuery, Core_Ad_Banner_WrapperQueryVariables>(
    adBannerQuery,
    {
      ssr: true,
      variables: { keywords: parseKeywords(url, url.query) },
    },
  );

  const { errors, loading } = remixerSearchResults;
  const listings = marketplaceListingsSearch?.listings;
  const pagination = marketplaceListingsSearch?.pagination || { total: 0, page: 1, pageSize: 45 };
  const offset = getOffsetFromPage(pagination.page.toString(), pagination.pageSize);

  const filters = marketplaceListingsSearch?.facetInfo;
  const facetGroups = filters?.facetGroups || [];

  const querySuggestions = querySuggestionsResponse?.querySuggestions?.queries?.[0]?.suggestions ?? [];
  const tipText = <BumpDSACompliancePopover />;

  if (marketplaceListingsSearch) { console.log('marketplace listings data', marketplaceListingsSearch); }

  function toggleListView() {
    setItem(LIST_VIEW_STATE, !listViewEnabled);
    setListViewEnabled(!listViewEnabled);
  }

  return (
    <div className="faceted-grid faceted-grid--wide">
      <div className="faceted-grid__inner">
        <div className="faceted-grid__main">
          <HeaderWithMetadata
            params={url.query}
            query={url.query?.query || ''}
          />
          {
            !adLoading && !!adResponse?.adServe?.ad?.imageAdCreative &&
            <div className="mt-6 mb-4 site-wrapper">
              <AdBanner
                ad={adResponse.adServe.ad}
              />
            </div>
          }
          <MarketplaceRelatedSearches
            querySuggestions={querySuggestions}
            loading={querySuggestionsLoading}
            relatedSearchLocation="top"
            marketplacePageV2
            displayPills
          />
          <SearchOverview
            facetGroups={facetGroups}
            loading={loading}
            query={url.query?.query || ''}
            resultsTotal={pagination.total}
            tipText={tipText}
            toggleListView={toggleListView}
          />
          <ListingGrid
            filters={[]} // this is only used for the sidebar which is rendered on mobile. we should be able to find a solution for web & mobile outside the listings grid later
            loading={loading}
            listings={listings}
            listingSearchTotal={pagination.total}
            error={!!errors?.length}
            isListView={listViewEnabled}
          />
          <RQLPagination
            limit={pagination.pageSize}
            total={pagination.total}
            offset={offset}
          />
          <MarketplaceRelatedSearches
            querySuggestions={querySuggestions}
            loading={querySuggestionsLoading}
            relatedSearchLocation="bottom"
            marketplacePageV2
          />
        </div>
      </div>
    </div>
  );
}

const MARKETPLACE_REMIXER_SEARCH = gql(`
  query Core_Marketplace_Remixer_Search(
    $input: MarketplaceSearchInput!
  ) {
    _trace {
      trace
    }
    marketplaceListingsSearch(input: $input) {
      query
      pagination {
        total
        page
        pageSize
      }
      facetInfo {
        facetGroups {
          all
          displayName
          key
          featured
          widgetType
          facets {
            all
            key
            count
            displayName
            searchUrl
            selected
            subFacets {
              all
              key
              count
              displayName
              searchUrl
              selected
              subFacets {
                all
                key
                count
                displayName
                searchUrl
                selected
              }
            }
          }
        }
      }
      listings {
        _id
        ...ListingCardFields
      }
    }
  }
`);

export default MarketplacePageV2;
